import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth, eUscitaVolontaria } from './context/AuthContext';
import './pages/pages.css';
import Login from './pages/Login';
import SetupStudio from './pages/SetupStudio';
import Dashboard from './pages/Dashboard';
import Lavoratori from './pages/Lavoratori';
import Clienti from './pages/Clienti';
import ConfigEmail from './pages/ConfigEmail';
import Comunicazioni from './pages/Comunicazioni';
import Statistiche from './pages/Statistiche';
import CambiaPassword from './pages/CambiaPassword';
import AccessoNegato from './pages/AccessoNegato';
import Utenti from './pages/Utenti';
import Cestino from './pages/Cestino';
import Hub from './pages/Hub';
import Scadenziario from './pages/scadenziario/Scadenziario';
import Ditte from './pages/scadenziario/Ditte';
import TipiCategorie from './pages/scadenziario/TipiCategorie';
import CodiciUnilav from './pages/scadenziario/CodiciUnilav';
import LivelliCcnl from './pages/scadenziario/LivelliCcnl';
import Competenze from './pages/competenze/Competenze';
import CestinoCompetenze from './pages/competenze/CestinoCompetenze';
import Anagrafiche from './pages/anagrafiche/Anagrafiche';
import SchedaAnagrafica from './pages/anagrafiche/SchedaAnagrafica';
import CestinoAnagrafiche from './pages/anagrafiche/CestinoAnagrafiche';
import Diciture from './pages/competenze/Diciture';
import ClientiStudio from './pages/competenze/ClientiStudio';
import Fatturanti from './pages/competenze/Fatturanti';
import ListinoPratiche from './pages/competenze/ListinoPratiche';
import TariffeOrarie from './pages/competenze/TariffeOrarie';
import ElenchiValori from './pages/competenze/ElenchiValori';
import ImpostazioniCompetenze from './pages/competenze/Impostazioni';
import { puoAccedere } from './utils/permessi';
import { destinazioneDopoLogin } from './utils/destinazione';

function ProtectedRoute({ children }) {
  const { utente, caricamento } = useAuth();
  const location = useLocation();
  if (caricamento) return <div style={{ padding: '2rem', textAlign: 'center' }}>Caricamento...</div>;
  // Ricorda l'indirizzo richiesto: dopo il login si torna lì
  if (!utente) {
    return <Navigate to="/login" replace state={eUscitaVolontaria() ? undefined : { da: location.pathname + location.search }} />;
  }
  // Con una password temporanea si può solo cambiarla
  if (utente.deve_cambiare_password && location.pathname !== '/cambia-password') {
    return <Navigate to="/cambia-password" replace />;
  }
  return children;
}

// Rotta di una sezione: senza permesso si vede la pagina "Accesso non consentito"
function RouteSezione({ sezione, children }) {
  const { utente } = useAuth();
  return (
    <ProtectedRoute>
      {puoAccedere(utente, sezione) ? children : <AccessoNegato />}
    </ProtectedRoute>
  );
}

// Rotta riservata agli admin
// Pagine per tutti gli operatori dello studio (non per i clienti); cosa si vede lo decide il server
function RouteStudio({ children }) {
  const { utente } = useAuth();
  return <ProtectedRoute>{utente?.ruolo === 'studio' ? children : <AccessoNegato />}</ProtectedRoute>;
}

function RouteAdmin({ children }) {
  const { utente } = useAuth();
  return <ProtectedRoute>{utente?.admin ? children : <AccessoNegato />}</ProtectedRoute>;
}

// Pagina iniziale: l'Hub per gli utenti studio; per i clienti la dashboard degli intermittenti, come prima
function Home() {
  const { utente } = useAuth();
  return utente.ruolo === 'studio' ? <Hub /> : <Dashboard />;
}

function PublicRoute({ children }) {
  const { utente, caricamento } = useAuth();
  const location = useLocation();
  if (caricamento) return <div style={{ padding: '2rem', textAlign: 'center' }}>Caricamento...</div>;
  // Già collegato (anche subito dopo il login): alla pagina richiesta, altrimenti alla pagina iniziale
  return utente ? <Navigate to={destinazioneDopoLogin(location)} replace /> : children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/setup" element={<PublicRoute><SetupStudio /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/intermittenti" element={<RouteSezione sezione="intermittenti"><Dashboard /></RouteSezione>} />
      <Route path="/clienti" element={<RouteSezione sezione="intermittenti"><Clienti /></RouteSezione>} />
      <Route path="/lavoratori" element={<RouteSezione sezione="intermittenti"><Lavoratori /></RouteSezione>} />
      <Route path="/comunicazioni" element={<RouteSezione sezione="intermittenti"><Comunicazioni /></RouteSezione>} />
      <Route path="/email" element={<RouteSezione sezione="intermittenti"><ConfigEmail /></RouteSezione>} />
      <Route path="/statistiche" element={<RouteSezione sezione="intermittenti"><Statistiche /></RouteSezione>} />
      <Route path="/competenze" element={<RouteSezione sezione="competenze"><Competenze /></RouteSezione>} />
      <Route path="/competenze/diciture" element={<RouteSezione sezione="competenze"><Diciture /></RouteSezione>} />
 <Route path="/competenze/listino" element={<RouteSezione sezione="competenze"><ListinoPratiche /></RouteSezione>} />
      <Route path="/competenze/clienti" element={<RouteSezione sezione="competenze"><ClientiStudio /></RouteSezione>} />
      <Route path="/competenze/fatturanti" element={<RouteAdmin><Fatturanti /></RouteAdmin>} />
      <Route path="/competenze/tariffe" element={<RouteAdmin><TariffeOrarie /></RouteAdmin>} />
      <Route path="/competenze/elenchi" element={<RouteAdmin><ElenchiValori /></RouteAdmin>} />
      <Route path="/competenze/cestino" element={<RouteSezione sezione="competenze"><CestinoCompetenze /></RouteSezione>} />
      <Route path="/competenze/impostazioni" element={<RouteAdmin><ImpostazioniCompetenze /></RouteAdmin>} />
      <Route path="/anagrafiche" element={<RouteSezione sezione="anagrafiche"><Anagrafiche /></RouteSezione>} />
      <Route path="/anagrafiche/cestino" element={<RouteSezione sezione="anagrafiche"><CestinoAnagrafiche /></RouteSezione>} />
      <Route path="/anagrafiche/:id" element={<RouteSezione sezione="anagrafiche"><SchedaAnagrafica /></RouteSezione>} />
      <Route path="/scadenziario" element={<RouteSezione sezione="scadenziario"><Scadenziario /></RouteSezione>} />
      <Route path="/scadenziario/ditte" element={<RouteSezione sezione="scadenziario"><Ditte /></RouteSezione>} />
      <Route path="/scadenziario/tipi" element={<RouteSezione sezione="scadenziario"><TipiCategorie /></RouteSezione>} />
      <Route path="/scadenziario/codici-unilav" element={<RouteSezione sezione="scadenziario"><CodiciUnilav /></RouteSezione>} />
      <Route path="/scadenziario/livelli-ccnl" element={<RouteSezione sezione="scadenziario"><LivelliCcnl /></RouteSezione>} />
      <Route path="/cestino" element={<RouteStudio><Cestino /></RouteStudio>} />
      <Route path="/utenti" element={<RouteSezione sezione="utenti"><Utenti /></RouteSezione>} />
      <Route path="/accesso-negato" element={<ProtectedRoute><AccessoNegato /></ProtectedRoute>} />
      <Route path="/cambia-password" element={<ProtectedRoute><CambiaPassword /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
